// @ts-check
/// <reference path="../../types/index.js" />

import React from 'react';
import Box from '@mui/material/Box';
import { usePrevious } from 'react-use';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useEffect, useState } from 'react';
import SaveAllDialog from './SaveAllDialog';
import AuditLogModal from '../AuditLogModal';
import hexToRGBA from '../../util/HEXToRGBA';
import { useHistory } from 'react-router-dom';
import Accordion from '@mui/material/Accordion';
import Typography from '@mui/material/Typography';
import { lazy, useContext, Suspense } from 'react';
import SaveAllExitDialog from './SaveAllExitDialog';
import useAuditLogs from '../../hooks/useAuditLogs';
import ListAltIcon from '@material-ui/icons/ListAlt';
import { makeStyles } from '@material-ui/core/styles';
import globalColors from '../../theme/GlobalColorsv2';
import BookmarkIcon from '@material-ui/icons/Bookmark';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import FormUpdatedContext from '../../contexts/FormUpdatedContext';
import SpecialNotes from '../../Pages/GalaxyCoreWindows/SpecialNotes';
import PatientSummary from '../../Pages/AccountDetails/PatientSummary';
import SelectTabContextProvider from '../SelectTabContext/SelectTabContext';
import ErrorBoundaryForComponents from '../../util/ErrorBoundaryForComponents';
import EligibilityScrubAndPronto from '../../Pages/GalaxyCoreWindows/EligibilityScrubAndPronto';
import ResetWindowDataContext from '../../contexts/ResetWindowDataContext';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { useSaveWindow } from '../../hooks/useSaveWindow';

const AccountNotes = lazy(() => import('../../Pages/GalaxyCoreWindows/AccountNotes'));

const MultiAccountInformation = lazy(() =>
  import('../../Pages/GalaxyCoreWindows/MultiAccountInformation'),
);

const ImagingW47 = lazy(() =>
  import('../../Pages/GalaxyCoreWindows/DocumentsManagement/ImagingW47'),
);

const PreviousAddressesW11 = lazy(() => import('../../Pages/GalaxyCoreWindows/RpAddressHistory'));

const AccountMessagesW44 = lazy(() => import('../../Pages/GalaxyCoreWindows/Messages'));

const AgentAdvocateInformation = lazy(() =>
  import('../../Pages/GalaxyCoreWindows/AgentAdvocateInformation'),
);

const GenericUDW = lazy(() =>
  import('../../Pages/AccountDetails/CustomTabs/UserDefinedWindows/GenericUDW'),
);

const AccountTie = lazy(() => import('../../Pages/GalaxyCoreWindows/AccountTie'));

const WorkFlowAndStatus = lazy(() => import('../../Pages/AccountDetails/WorkFlowAndStatus'));

const AddaNote = lazy(() => import('../AddaNotes'));

const MyCoverageCare = lazy(() => import('../../Pages/GalaxyCoreWindows/MyCoverageCare'));

const GuarantorW61 = lazy(() => import('../../v1/windows/AccountGuarantor'));

const Demographics = lazy(() => import('../../Pages/GalaxyCoreWindows/CustomerDemographics'));

const BillingIndicator = lazy(() => import('../../Pages/GalaxyCoreWindows/BillingIndicator'));

const Communications = lazy(() => import('../../Pages/GalaxyCoreWindows/CommunicationHistory'));

const ClientInformationW13 = lazy(() => import('../../Pages/GalaxyCoreWindows/ClientInformation'));

const DispositionTimeline = lazy(() => import('../../Pages/GalaxyCoreWindows/DispositionTimeline'));

const CbsWindow = lazy(() => import('../../v2/windows/CBS'));

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    width: '100%',
    borderRadius: '5px',
  },
  padding: {
    padding: theme.spacing(3),
  },
  demo1: {
    backgroundColor: '#296bff',
    color: 'white',
    borderRadius: '6px',
  },
}));

export default function FocusedWindows({ tempReRenderWindow, setTempReRenderWindow }) {
  /**
   * Using the useContext hook to access context from SelectTabContextProvider.
   * @type {SelectTabContextValue}
   */
  const { label, auditLogs, setAuditLogs, focusedWindows, defaultWindows, setFocusedWindows } =
    useContext(SelectTabContextProvider);

  /**
   * @type {FormUpdatedContextType}
   */
  const { updatedForms, removeFormCode } = useContext(FormUpdatedContext);

  /**
   * @type {ResetWindowDataContextType}
   */
  const { triggerResetByLabel } = useContext(ResetWindowDataContext);

  /**
   * Selects role data from the Redux state.
   * @param {ReduxState} state - The Redux state.
   * @return {RoleData} - The role data.
   */
  const selectRoleData = ({ role }) => role;
  const { roleData } = useSelector(selectRoleData);

  /**
   * Retrieves UD Window data from the Redux store.
   * @param {ReduxState} state - The Redux state.
   * @return {UdwData} The UD Window data.
   */
  const selectUDWData = ({ udwWindows }) => udwWindows;
  const { udwData } = useSelector(selectUDWData);

  /**
   * Material-UI makeStyles hook to generate and use styles for the component.
   * @type {ReturnType<typeof makeStyles>}
   */
  const classes = useStyles();

  /**
   * React Router `useLocation` hook to get the current location object, including pathname and state.
   * @type {import('react-router').Location}
   */
  const location = useLocation();
  /**
   * Hook to fetch audit logs for specific windows.
   * @type {() => (label: string, count: number) => void}
   */
  const { fetchAuditLogs, loadingWindow } = useAuditLogs();

  /**
   * Custom hook to retain the previous state of focused windows.
   * @type {Array<WindowType> | undefined}
   */
  const prevFocusedWindows = usePrevious(focusedWindows);

  /**
   * State to manage whether the Audit Log modal dialog is open.
   * @type {[boolean, (value: boolean) => void]}
   */
  const [isLogModalOpened, setIsLogModalOpened] = useState(false);

  /**
   * State to manage whether the "Save All" dialog is open.
   * @type {[boolean, (value: boolean) => void]}
   */
  const [openSaveAllDialog, setOpenSaveAllDialog] = useState(false);

  /**
   * State to manage whether the "Exit" dialog.
   * @type {[boolean, (value: boolean) => void]}
   */
  const [openExitDialog, setOpenExitDialog] = useState(false);

  /**
   * State to manage the next path that the user wnats to navigate to.
   * @type {[string, (value: string) => void]}
   */
  const [nextPath, setNextPath] = useState('');

  const history = useHistory();

  /**
   * Closes the "Save All" dialog by setting the `openSaveAllDialog` state to `false`.
   * @return {void}
   */
  const handleCloseSaveAllDialog = () => setOpenSaveAllDialog(false);

  /**
   * Saves the window when the save button is clicked
   * @param {string} code - Identifier for the Window. windowName
   * @return {void}
   */
  const { saveWindow } = useSaveWindow();

  /**
   * Resets the window when the save button is clicked
   * @param {string} code - Identifier for the Window. windowName
   * @return {void}
   */
  const resetWindow = (code) => {
    triggerResetByLabel(code);
    removeFormCode(code);
  };

  /**
   * Opens the Audit Log modal dialog for a specific window label.
   * Marks the relevant log as active while deactivating others, and sets the `isLogModalOpened` state to `true`.
   *
   * @param {MouseEvent} event - The event triggered by clicking the log icon, used to prevent event bubbling.
   * @param {string} label - The label of the window whose audit logs are being displayed.
   * @return {void}
   */
  const openLogModal = (event, label) => {
    event.stopPropagation();

    setAuditLogs((logs) =>
      logs.map((log) => {
        log.active = false;
        return log.label === label ? { ...log, active: true } : log;
      }),
    );
    setIsLogModalOpened(true);
  };

  /**
   * Closes the Audit Log modal dialog by setting the `isLogModalOpened` state to `false`.
   * @return {void}
   */
  const closeLogModal = () => setIsLogModalOpened(false);

  /**
   * useEffect to reset the focused windows to default when none are focused.
   */
  useEffect(() => {
    if (focusedWindows.length) {
      return;
    }

    setFocusedWindows(defaultWindows);
  }, []);

  /**
   * useEffect to fetch audit logs when a new focused window is added
   * and there's a valid account number in the location state.
   */
  useEffect(() => {
    if (!location?.state?.accountNumber) {
      return;
    }

    if (!prevFocusedWindows) {
      return;
    }

    const newWindows = focusedWindows.filter(
      (window) => !prevFocusedWindows.some((prevWindow) => prevWindow.label === window.label),
    );

    if (newWindows.length <= 0) {
      return;
    }

    const labels = newWindows.map((focusedWindow) => focusedWindow.label);
    labels.forEach((label) => fetchAuditLogs(label, 0));
  }, [focusedWindows]);

  // State to track expanded state of each accordion
  const [expandedStates, setExpandedStates] = useState(
    focusedWindows.reduce((acc, window) => {
      acc[window.code] = false;
      return acc;
    }, {}),
  );

  // Toggle function for each accordion
  const handleToggle = (code) => {
    setExpandedStates((prevStates) => ({
      ...prevStates,
      [code]: !prevStates[code], // Toggle the state of the clicked accordion
    }));
  };

  // Function to close all accordions
  const closeAllAccordions = () => {
    setExpandedStates(
      Object.keys(expandedStates).reduce((acc, key) => {
        acc[key] = false; // Set all states to false
        return acc;
      }, {}),
    );
  };

  /**
   * useEffect to open the "Save All" dialog when more than two forms are updated,
   * and the dialog is not already open.
   */
  useEffect(() => {
    if (updatedForms.length > 1 && !openSaveAllDialog) {
      setOpenSaveAllDialog(true);
    }

    if (!updatedForms.length && openSaveAllDialog) {
      closeAllAccordions();
      handleCloseSaveAllDialog();
    }

    const unblock = history.block((location, action) => {
      if (updatedForms.length) {
        setOpenExitDialog(true);
        setNextPath(location.pathname);
        return false;
      }

      return true;
    });

    return () => {
      unblock();
    };
  }, [updatedForms, history]);

  const getCurrentTab = (currentSelectedTab) => {
    let isCustomWindow = false;
    if (
      typeof roleData !== 'undefined' &&
      typeof roleData?.window === 'object' &&
      typeof roleData?.window['Custom Window'] === 'object' &&
      roleData?.window['Custom Window'].hasOwnProperty(currentSelectedTab.code)
    ) {
      isCustomWindow = true;
    }

    if (isCustomWindow) {
      const windowSelected = udwData?.filter((window) => {
        if (window?.windowName === currentSelectedTab?.code) {
          return true;
        }

        return false;
      })[0];

      return (
        <ErrorBoundaryForComponents>
          {tempReRenderWindow && (
            <GenericUDW
              label={label}
              currentLabel={currentSelectedTab?.label}
              header={windowSelected?.windowName}
              windowCode={windowSelected?.windowId}
              setTempReRenderWindow={setTempReRenderWindow}
            />
          )}
        </ErrorBoundaryForComponents>
      );
    }

    if (currentSelectedTab?.code === 'Account Status') {
      return (
        <ErrorBoundaryForComponents>
          <WorkFlowAndStatus label={'Account Status'} />
        </ErrorBoundaryForComponents>
      );
    }

    // No Saving functionality in this form. Informational only.
    if (currentSelectedTab?.code === '0') {
      return <PatientSummary />;
    }

    if (currentSelectedTab?.code === '10') {
      return <SpecialNotes />;
    }

    // No Saving functionality in this form. Its an upload form.
    if (currentSelectedTab?.code === 'W47') {
      return (
        <ErrorBoundaryForComponents>
          <ImagingW47 label={label} />
        </ErrorBoundaryForComponents>
      );
    }

    // Unable to update form. Fields are disabled by default.
    if (currentSelectedTab?.code === 'W61') {
      return (
        <ErrorBoundaryForComponents>
          <GuarantorW61 label={label} currentLabel={currentSelectedTab?.label} />
        </ErrorBoundaryForComponents>
      );
    }

    // Unable to update form. Fields are disabled by default.
    if (currentSelectedTab?.code === 'W13') {
      return (
        <ErrorBoundaryForComponents>
          <ClientInformationW13 />
        </ErrorBoundaryForComponents>
      );
    }

    // Unable to update form. Read only form.
    if (currentSelectedTab?.code === 'W11') {
      return (
        <ErrorBoundaryForComponents>
          <PreviousAddressesW11 label={label} currentLabel={currentSelectedTab?.label} />
        </ErrorBoundaryForComponents>
      );
    }

    // Unable to update form. Fields are disabled by default.
    if (currentSelectedTab?.code === 'AGENT_INFO') {
      return (
        <ErrorBoundaryForComponents>
          <AgentAdvocateInformation label={label} currentLabel={currentSelectedTab?.label} />
        </ErrorBoundaryForComponents>
      );
    }

    // Unable to update form. Form is a table.
    if (currentSelectedTab?.code === 'W44') {
      return (
        <ErrorBoundaryForComponents>
          <AccountMessagesW44 label={label} currentLabel={currentSelectedTab?.label} />
        </ErrorBoundaryForComponents>
      );
    }

    // Unable to update form. Just informational tabs
    if (currentSelectedTab?.code === 'W52,W95') {
      return (
        <ErrorBoundaryForComponents>
          <Communications />
        </ErrorBoundaryForComponents>
      );
    }

    // Unable to update form. Just informational tabs
    if (currentSelectedTab?.code === 'W2,W4') {
      return (
        <ErrorBoundaryForComponents>
          <Demographics />
        </ErrorBoundaryForComponents>
      );
    }

    if (currentSelectedTab?.code === 'W8') {
      return (
        <ErrorBoundaryForComponents>
          <AddaNote label={label} currentLabel={currentSelectedTab?.label} />
        </ErrorBoundaryForComponents>
      );
    }

    if (currentSelectedTab?.code === 'W6') {
      return (
        <ErrorBoundaryForComponents>
          <CbsWindow label={label} currentLabel={currentSelectedTab?.label} />
        </ErrorBoundaryForComponents>
      );
    }

    // No Saving functionality in this form. Shows notes from previous form
    if (currentSelectedTab?.code === 'W32') {
      return (
        <ErrorBoundaryForComponents>
          <AccountNotes label={label} currentLabel={currentSelectedTab?.label} />
        </ErrorBoundaryForComponents>
      );
    }

    // Unable to update form. Not showing anything.
    if (currentSelectedTab?.code === 'ST') {
      return (
        <ErrorBoundaryForComponents>
          <DispositionTimeline label={label} currentLabel={currentSelectedTab?.label} />
        </ErrorBoundaryForComponents>
      );
    }

    // Unable to update form. Fields are disabled by default.
    if (currentSelectedTab?.code === 'CW_W3') {
      return (
        <ErrorBoundaryForComponents>
          <MultiAccountInformation label={label} currentLabel={currentSelectedTab?.label} />
        </ErrorBoundaryForComponents>
      );
    }

    // Unable to update form. Informational
    if (currentSelectedTab?.code === 'W51') {
      return (
        <ErrorBoundaryForComponents>
          <AccountTie label={label} currentLabel={currentSelectedTab?.label} />
        </ErrorBoundaryForComponents>
      );
    }

    // Form opens a dialog. No need to add Save & Cancel in the accordion header
    if (currentSelectedTab?.code === 'BI') {
      return (
        <ErrorBoundaryForComponents>
          <BillingIndicator label={label} currentLabel={currentSelectedTab?.label} />
        </ErrorBoundaryForComponents>
      );
    }

    // Unable to update form. Just informational tabs
    if (currentSelectedTab?.code === 'MCC') {
      return (
        <ErrorBoundaryForComponents>
          <MyCoverageCare />
        </ErrorBoundaryForComponents>
      );
    }

    // Unable to update form. Table with information
    if (currentSelectedTab?.code === 'ESP') {
      return (
        <ErrorBoundaryForComponents>
          <EligibilityScrubAndPronto label={label} currentLabel={currentSelectedTab?.label} />
        </ErrorBoundaryForComponents>
      );
    }
  };

  return (
    <div className={classes.subContent}>
      <AuditLogModal open={isLogModalOpened} handleClose={closeLogModal} />
      {focusedWindows.map((currentSelectedTab) => (
        <Accordion
          expanded={expandedStates[currentSelectedTab.code]}
          onChange={() => handleToggle(currentSelectedTab.code)}
          key={currentSelectedTab.code}
          sx={{
            margin: '4px',
            borderRadius: '4px',
            '&.Mui-expanded': {
              margin: '4px !important',
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={currentSelectedTab?.label}
            id={currentSelectedTab?.code}
            sx={{
              backgroundColor: globalColors.grey,
              borderRadius: '4px',
              height: 56,
              minHeight: 56,
              '&.Mui-expanded': {
                minHeight: 56,
                height: 56,
              },
            }}
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {defaultWindows.some((window) => window.code === currentSelectedTab.code) && (
                <BookmarkIcon
                  style={{
                    height: '20px',
                    color: hexToRGBA('#3B4149', '.75'),
                    marginRight: '5px',
                    marginLeft: '-5px',
                  }}
                />
              )}
              <Typography
                sx={{
                  flex: 1,
                  textAlign: 'left',
                  fontSize: '16px',
                  fontWeight: '700',
                }}
              >
                {currentSelectedTab.label}
              </Typography>
              {updatedForms.some((form) => form === currentSelectedTab.code) && (
                <Stack
                  spacing={1}
                  direction="row"
                  sx={{
                    marginRight: '10px',
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => {
                      resetWindow(currentSelectedTab.code);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => {
                      saveWindow(currentSelectedTab.code);
                    }}
                  >
                    Save
                  </Button>
                </Stack>
              )}
              {loadingWindow[currentSelectedTab.label] === true ? (
                <CircularProgress style={{ color: 'rgb(44, 21, 112)' }} size={24} />
              ) : (
                auditLogs.some((auditLog) => auditLog.label === currentSelectedTab.label) && (
                  <Tooltip title="Audit Log" placement="top" arrow>
                    <ListAltIcon
                      onClick={(event) => openLogModal(event, currentSelectedTab.label)}
                      style={{ marginRight: '10px' }}
                    />
                  </Tooltip>
                )
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              width: '100%',
              p: currentSelectedTab.code === 'W6' ? '0px 0px 16px 0px' : '8px 16px 16px',
            }}
          >
            <Typography sx={{ width: '100%' }}>
              <Suspense
                fallback={<div style={{ textAlign: 'center', fontSize: '1.4rem' }}>Loading...</div>}
              >
                {getCurrentTab(currentSelectedTab)}
              </Suspense>
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}
      <SaveAllDialog open={openSaveAllDialog} handleClose={handleCloseSaveAllDialog} />
      <SaveAllExitDialog
        open={openExitDialog}
        nextPath={nextPath}
        handleClose={() => {
          setOpenExitDialog(false);
        }}
      />
    </div>
  );
}
